// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Button, Collapse, Form } from "react-bootstrap";
import InputNumber from "rc-input-number";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import { DeviceType, AlarmSource, isStratoDevice } from "shared/types/globtypes";
import { AlarmSettings } from "shared/types/cloudtypes";
import { DataState } from "types";
import { isSet } from "shared/utils/utils";
import { IAction } from "actions";
import { IState } from "states/states";
import { IOcppCharger, IRfid } from "states/ocppstates";
import { Site } from "states/sitestates";
import { SiteSettingsArea } from "states/guistates";

import CheckList from "view/comp/CheckList";
import IconExpandMore from "icons/iconExpandMore";
import IconExpandLess from "icons/iconExpandLess";

interface IAlarmSettingsExtProps {
  settings: AlarmSettings | undefined; // include this to ensure screen update
  onChange: (alarmSettings: AlarmSettings) => void;
}

interface IAlarmSettingsProps {
  site: Site | undefined;
  dataState: DataState;
  chargers: IOcppCharger[];
  rfids: IRfid[];
  hasOcppChargers: boolean;
  openedSiteSettingsArea: SiteSettingsArea[] | undefined;
  isAlarmExpanded: boolean;
}

const mapStateToProps = (state: IState, ownProps: IAlarmSettingsExtProps) => {
  const siteId = state.cached.selectedSite;
  const site = state.site.siteList?.find((curSite) => curSite.id === siteId);
  const chargers = state.ocpp.charger.filter((curItem) => isSet(curItem.fk_charger));
  const props: IAlarmSettingsProps = {
    site,
    dataState: state.scheduler.saveState,
    chargers,
    rfids: state.ocpp.rfid,
    hasOcppChargers: chargers.length > 0,
    openedSiteSettingsArea: state.gui.openedSiteSettingsArea,
    isAlarmExpanded: state.gui.openedSiteSettingsArea?.includes(SiteSettingsArea.ALARM) ?? false,
  };
  return props;
};

interface IAlarmSettingsDispatch {
  onSiteSettingsAreaToggle: (areas: SiteSettingsArea[] | undefined, area: SiteSettingsArea) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IAlarmSettingsDispatch = {
    onSiteSettingsAreaToggle: (areas: SiteSettingsArea[] | undefined, area: SiteSettingsArea) => {
      if (!areas) {
        areas = [area];
      } else {
        areas = areas.includes(area) ? [...areas.filter((a) => a !== area)] : [...areas, area];
      }
      dispatch({
        type: "ACT_SITE_SETTINGS_AREA_TOGGLE",
        payload: areas,
      });
    },
  };
  return dispatchProps;
};

// default pause duration offered when the charging suspend trigger gets enabled
const DEFAULT_PAUSE_TIMEOUT = 600; // s
// default SoC limit offered when the SoC filter gets enabled
const DEFAULT_SOC_LIMIT = 80; // %

interface IAlarmSettingsState {
  // keep the filter lists open when all their entries get unchecked (= no filter)
  isChargerListOpen: boolean;
  isRfidListOpen: boolean;
}

class AlarmSettingsComponent extends React.Component<
  IAlarmSettingsExtProps & IAlarmSettingsProps & IAlarmSettingsDispatch & RoutedProps,
  IAlarmSettingsState
> {
  state: IAlarmSettingsState = {
    isChargerListOpen: false,
    isRfidListOpen: false,
  };

  public render() {
    const isEditMode = true;

    return (
      <>
        <h1>
          <span
            className="link"
            onClick={() => {
              this.props.onSiteSettingsAreaToggle(
                this.props.openedSiteSettingsArea,
                SiteSettingsArea.ALARM,
              );
            }}
          >
            <Button variant="link" className="expand-button me-2 d-print-none">
              {this.props.isAlarmExpanded ?
                <IconExpandMore />
              : <IconExpandLess />}
            </Button>
            <FormattedMessage id="Alarm Settings" />
          </span>
        </h1>
        <Collapse in={this.props.isAlarmExpanded} className="fullwidthwhite">
          <div>
            <table>
              <thead>
                <tr className="mt-2">
                  <th colSpan={3}>
                    <FormattedMessage id="Activated Alarms" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {this.props.hasOcppChargers && this.renderChargerAlarms()}
                {this.props.site?.deviceType !== DeviceType.VIRTUAL && (
                  <>
                    <tr className="mt-2">
                      <td
                        rowSpan={this.props.settings?.offlineTimeout ? 2 : 1}
                        style={{ verticalAlign: "top" }}
                        onClick={(event) => {
                          this.props.onChange({
                            ...this.props.settings,
                            offlineTimeout:
                              (
                                !isSet(this.props.settings?.offlineTimeout) ||
                                this.props.settings?.offlineTimeout === 0
                              ) ?
                                3600
                              : 0,
                          });
                        }}
                      >
                        <FormattedMessage id={`AlarmSource_${AlarmSource.Offline}`} />
                      </td>
                      <td colSpan={2}>
                        {isEditMode ?
                          <Form.Check
                            name="offlineAlarmEnabled"
                            id="offlineAlarmEnabled"
                            checked={(this.props.settings?.offlineTimeout ?? 0) > 0}
                            onChange={(event) => {
                              this.props.onChange({
                                ...this.props.settings,
                                offlineTimeout: event?.currentTarget.checked ? 3600 : 0,
                              });
                            }}
                            label={<FormattedMessage id="Enabled" />}
                          />
                        : this.props.settings?.offlineTimeout ?
                          <FormattedMessage id="enabled" />
                        : <FormattedMessage id="disabled" />}
                      </td>
                    </tr>
                    {(this.props.settings?.offlineTimeout ?? 0) > 0 && (
                      <tr>
                        <td className="pe-2">
                          <FormattedMessage id="Timeout" />:
                        </td>
                        <td>
                          {isEditMode ?
                            <>
                              <InputNumber
                                type="number"
                                className="ms-1 default-border"
                                style={{
                                  marginTop: -3,
                                  display: "inline-block",
                                }}
                                value={
                                  this.props.settings?.offlineTimeout ?
                                    this.props.settings?.offlineTimeout / 3600
                                  : undefined
                                }
                                min={1}
                                disabled={this.props.dataState === DataState.SAVE_REQ}
                                onChange={(number) => {
                                  if (isSet(number)) {
                                    this.props.onChange({
                                      ...this.props.settings,
                                      offlineTimeout: number * 3600,
                                    });
                                  }
                                }}
                              />{" "}
                              <FormattedMessage id="hour(s)" />
                            </>
                          : this.props.settings?.offlineTimeout}
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </Collapse>
      </>
    );
  }

  private renderChargerAlarms() {
    const isEditMode = true;

    return (
      <>
        <tr className="mt-3">
          <td
            rowSpan={this.props.settings?.chargerOfflineTimeout ? 2 : 1}
            style={{ verticalAlign: "top" }}
            onClick={(event) => {
              this.props.onChange({
                ...this.props.settings,
                chargerOfflineTimeout:
                  (
                    !isSet(this.props.settings?.chargerOfflineTimeout) ||
                    this.props.settings?.chargerOfflineTimeout === 0
                  ) ?
                    3600
                  : 0,
              });
            }}
          >
            <FormattedMessage id={`AlarmSource_${AlarmSource.OcppChargerOffline}`} />
          </td>
          <td colSpan={2}>
            {isEditMode ?
              <Form.Check
                name="chargerOfflineEnabled"
                id="chargerOfflineEnabled"
                checked={(this.props.settings?.chargerOfflineTimeout ?? 0) > 0}
                onChange={(event) => {
                  this.props.onChange({
                    ...this.props.settings,
                    chargerOfflineTimeout: event?.currentTarget.checked ? 3600 : 0,
                  });
                }}
                label={<FormattedMessage id="Enabled" />}
              />
            : this.props.settings?.chargerOfflineTimeout ?
              <FormattedMessage id="enabled" />
            : <FormattedMessage id="disabled" />}
          </td>
        </tr>
        {(this.props.settings?.chargerOfflineTimeout ?? 0) > 0 && (
          <tr>
            <td className="pe-2">
              <FormattedMessage id="Timeout" />:
            </td>
            <td>
              {isEditMode ?
                <>
                  <InputNumber
                    type="number"
                    className="ms-1 default-border"
                    value={
                      this.props.settings?.chargerOfflineTimeout ?
                        this.props.settings?.chargerOfflineTimeout / 3600
                      : undefined
                    }
                    min={1}
                    disabled={this.props.dataState === DataState.SAVE_REQ}
                    onChange={(number) => {
                      if (isSet(number)) {
                        this.props.onChange({
                          ...this.props.settings,
                          chargerOfflineTimeout: number * 3600,
                        });
                      }
                    }}
                  />{" "}
                  <FormattedMessage id="hour(s)" />
                </>
              : this.props.settings?.chargerOfflineTimeout}
            </td>
          </tr>
        )}

        <tr className="mt-2">
          <td
            style={{ verticalAlign: "top" }}
            onClick={(event) => {
              this.props.onChange({
                ...this.props.settings,
                chargerFaultedTimeout:
                  (
                    !isSet(this.props.settings?.chargerFaultedTimeout) ||
                    this.props.settings?.chargerFaultedTimeout === 0
                  ) ?
                    3600
                  : 0,
              });
            }}
          >
            <FormattedMessage id={`AlarmSource_${AlarmSource.OcppChargerFaulted}`} />
          </td>
          <td colSpan={2}>
            {isEditMode ?
              <Form.Check
                name="chargerFaultedEnabled"
                id="chargerFaultedEnabled"
                checked={(this.props.settings?.chargerFaultedTimeout ?? 0) > 0}
                onChange={(event) => {
                  this.props.onChange({
                    ...this.props.settings,
                    chargerFaultedTimeout: event?.currentTarget.checked ? 3600 : 0,
                  });
                }}
                label={<FormattedMessage id="Enabled" />}
              />
            : this.props.settings?.chargerFaultedTimeout ?
              <FormattedMessage id="enabled" />
            : <FormattedMessage id="disabled" />}
          </td>
        </tr>

        {this.renderChargingInterruptedAlarm()}
      </>
    );
  }

  // the charging interrupted alarm triggers on paused and on stopped charging sessions,
  // both can be limited to cars below a given SoC and to specific chargers or RFID cards
  private renderChargingInterruptedAlarm() {
    const settings = this.props.settings;
    const isPausedEnabled = this.isPausedEnabled();
    const isStoppedEnabled = this.isStoppedEnabled();
    const isAlarmEnabled = isPausedEnabled || isStoppedEnabled;
    // both triggers share one SoC limit in the gui
    const socLimit = settings?.chargingInterruptedSoc ?? settings?.chargingEndedSoc;
    // the two trigger rows plus the pause duration, the SoC limit and the two filter rows
    const rowCount =
      2 + (isPausedEnabled ? 1 : 0) + (isAlarmEnabled ? 3 + (isSet(socLimit) ? 1 : 0) : 0);

    return (
      <>
        <tr className="mt-2">
          <td rowSpan={rowCount} style={{ verticalAlign: "top" }}>
            <FormattedMessage id={`AlarmSource_${AlarmSource.OcppChargingInterrupted}`} />
          </td>
          <td colSpan={2}>
            <Form.Check
              name="chargingSuspendedEnabled"
              id="chargingSuspendedEnabled"
              checked={isPausedEnabled}
              onChange={(event) =>
                this.changeChargingAlarm({ isPaused: event?.currentTarget.checked })
              }
              label={<FormattedMessage id="Alarm at Charging Suspend" />}
            />
          </td>
        </tr>
        {isPausedEnabled && (
          <tr>
            <td className="pe-2">
              <FormattedMessage id="Pause Duration" />:
            </td>
            <td>
              <InputNumber
                type="number"
                className="ms-1 default-border"
                style={{ marginTop: -3, display: "inline-block" }}
                value={(settings?.chargingPausedTimeout ?? DEFAULT_PAUSE_TIMEOUT) / 60}
                min={1}
                disabled={this.props.dataState === DataState.SAVE_REQ}
                onChange={(number) => {
                  if (isSet(number)) {
                    this.changeChargingAlarm({ pauseTimeout: number * 60 });
                  }
                }}
              />{" "}
              <FormattedMessage id="minute(s)" />
            </td>
          </tr>
        )}
        <tr>
          <td colSpan={2}>
            <Form.Check
              name="chargingEndedEnabled"
              id="chargingEndedEnabled"
              checked={isStoppedEnabled}
              onChange={(event) =>
                this.changeChargingAlarm({ isStopped: event?.currentTarget.checked })
              }
              label={<FormattedMessage id="Alarm at Charging Finish" />}
            />
          </td>
        </tr>
        {isAlarmEnabled && (
          <>
            <tr>
              <td colSpan={2}>
                <Form.Check
                  name="chargingSocLimitEnabled"
                  id="chargingSocLimitEnabled"
                  checked={isSet(socLimit)}
                  onChange={(event) =>
                    this.changeChargingAlarm({
                      soc:
                        event?.currentTarget.checked ? (socLimit ?? DEFAULT_SOC_LIMIT) : undefined,
                    })
                  }
                  label={<FormattedMessage id="Alarm only below SoC" />}
                />
              </td>
            </tr>
            {isSet(socLimit) && (
              <tr>
                <td className="pe-2">
                  <FormattedMessage id="Minimum SoC" />:
                </td>
                <td>
                  <InputNumber
                    type="number"
                    className="ms-1 default-border"
                    style={{ marginTop: -3, display: "inline-block" }}
                    value={socLimit}
                    min={1}
                    max={99}
                    disabled={this.props.dataState === DataState.SAVE_REQ}
                    onChange={(number) => {
                      if (isSet(number)) {
                        this.changeChargingAlarm({ soc: number });
                      }
                    }}
                  />{" "}
                  %
                </td>
              </tr>
            )}
            <tr>
              <td className="pe-2" style={{ verticalAlign: "top" }}>
                <FormattedMessage id="Chargers" />:
              </td>
              <td>{this.renderChargerFilter()}</td>
            </tr>
            <tr>
              <td className="pe-2" style={{ verticalAlign: "top" }}>
                <FormattedMessage id="RFID" />:
              </td>
              <td>{this.renderRfidFilter()}</td>
            </tr>
          </>
        )}
      </>
    );
  }

  // the alarm may be limited to single chargers, an empty list covers all of them
  private renderChargerFilter() {
    const chargerIds = this.props.settings?.chargingInterruptedChargers ?? [];
    const isListOpen = chargerIds.length > 0 || this.state.isChargerListOpen;

    return (
      <>
        <Form.Check
          inline
          className="ms-1"
          name="chargingAlarmAllChargers"
          id="chargingAlarmAllChargers"
          checked={!isListOpen}
          onChange={(event) => {
            const isAll = event.currentTarget.checked;
            this.setState({ isChargerListOpen: !isAll });
            this.props.onChange({
              ...this.props.settings,
              chargingInterruptedChargers:
                isAll ? undefined : this.props.chargers.map((curCharger) => curCharger.fk_charger),
            });
          }}
          label={<FormattedMessage id="all chargers" />}
        />
        {isListOpen && (
          <CheckList
            id="chargingAlarmChargers"
            className="mt-2"
            options={this.props.chargers.map((curCharger) => ({
              value: curCharger.fk_charger,
              label: curCharger.name ?? curCharger.chargepoint.split("@")[0],
            }))}
            selected={chargerIds}
            onChange={(chargingInterruptedChargers) =>
              this.props.onChange({ ...this.props.settings, chargingInterruptedChargers })
            }
          />
        )}
      </>
    );
  }

  // the alarm may be limited to single RFID cards, an empty list covers all of them
  private renderRfidFilter() {
    const rfidIds = this.props.settings?.chargingInterruptedRfids ?? [];
    const isListOpen = rfidIds.length > 0 || this.state.isRfidListOpen;

    return (
      <>
        <Form.Check
          inline
          className="ms-1"
          name="chargingAlarmAllRfids"
          id="chargingAlarmAllRfids"
          checked={!isListOpen}
          onChange={(event) => {
            const isAll = event.currentTarget.checked;
            this.setState({ isRfidListOpen: !isAll });
            this.props.onChange({
              ...this.props.settings,
              chargingInterruptedRfids:
                isAll ? undefined : this.props.rfids.map((curRfid) => curRfid.id),
            });
          }}
          label={<FormattedMessage id="all cards" />}
        />
        {isListOpen && (
          <CheckList
            id="chargingAlarmRfids"
            className="mt-2"
            options={this.props.rfids.map((curRfid) => ({
              value: curRfid.id,
              label: curRfid.name ?? curRfid.rfid,
            }))}
            selected={rfidIds}
            onChange={(chargingInterruptedRfids) =>
              this.props.onChange({ ...this.props.settings, chargingInterruptedRfids })
            }
          />
        )}
      </>
    );
  }

  // legacy settings only define the SoC limits, which enable their trigger as well;
  // this matches isChargingInterruptedAlarmEnabled() of the cloud engine
  private isPausedEnabled() {
    return (
      (this.props.settings?.chargingPausedTimeout ?? 0) > 0 ||
      (this.props.settings?.chargingInterruptedSoc ?? 0) > 0
    );
  }

  private isStoppedEnabled() {
    return (
      (this.props.settings?.chargingStoppedAlarm ?? false) ||
      (this.props.settings?.chargingEndedSoc ?? 0) > 0
    );
  }

  /*
    Store a change of the charging interrupted alarm. The triggers and their shared SoC limit
    are kept in separate settings: as a SoC limit also enables its trigger in the cloud engine,
    it is only stored for the enabled triggers.
  */
  private changeChargingAlarm(change: {
    isPaused?: boolean;
    isStopped?: boolean;
    pauseTimeout?: number;
    soc?: number;
  }) {
    const settings = this.props.settings;
    const isPaused = change.isPaused ?? this.isPausedEnabled();
    const isStopped = change.isStopped ?? this.isStoppedEnabled();
    const pauseTimeout =
      change.pauseTimeout ?? settings?.chargingPausedTimeout ?? DEFAULT_PAUSE_TIMEOUT;
    const soc =
      "soc" in change ?
        change.soc
      : (settings?.chargingInterruptedSoc ?? settings?.chargingEndedSoc);

    this.props.onChange({
      ...settings,
      chargingPausedTimeout: isPaused ? pauseTimeout : undefined,
      chargingStoppedAlarm: isStopped ? true : undefined,
      chargingInterruptedSoc: isPaused ? soc : undefined,
      chargingEndedSoc: isStopped ? soc : undefined,
    });
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AlarmSettingsComponent));
